import { ReactNode, Suspense } from "react"
import { AnySFFRagDocumentResult, SFFRagDocumentResultTypes, SFFRagDocumentTypeName } from "../../server/search/rag"
import { UseProgramFilmsQuery } from "@generated-types"
import { HeroSearchWidget } from "./cards/HeroSearchWidget"
import { Button } from "@components/ui/Button"
import { RelatedFilmsWidget } from "./cards/RelatedFilmsWidget"
import { Chip } from "@components/ui/Chip"
import { getFilmMeta } from "@features/program/get-film-meta"
import { ContentBlock, SingleContentBlock } from "eddev/blocks"
import { MapPinIcon, PhoneIcon } from "@heroicons/react/24/outline"

export type SearchWidgetInstance = {
  key: string
  score: number
  node: ReactNode
  placement: "side" | "center"
  presentingResults?: { type: SFFRagDocumentTypeName; id: number }[]
  maximum?: number
}

export type SearchWidgetKey = keyof typeof SEARCH_WIDGETS

export type SearchWidgetType = {
  maybeGetWidget(args: {
    isMobile: boolean
    isQuickSearch: boolean
    types?: SFFRagDocumentTypeName[]
    documents: AnySFFRagDocumentResult[]
    program?: UseProgramFilmsQuery
    appData: AppData
  }): SearchWidgetInstance | void
}

export const SEARCH_WIDGETS: Record<string, SearchWidgetType> = {
  venueHeroCard: {
    maybeGetWidget({ isMobile, isQuickSearch, types, documents }) {
      if (isMobile) return
      if (types?.length! > 0 && !types?.includes("venue") && !types?.includes("film")) return
      const heroVenue = documents.slice(0, 4).find((doc) => doc.type === "venue")
      if (heroVenue) {
        const data = heroVenue.data
        const address = JSON.parse(data?.info?.address || "{}")
        return {
          score: heroVenue.score * 1.1,
          maximum: 1,
          presentingResults: isMobile || isQuickSearch ? [heroVenue] : [],
          key: "venueHeroCard_" + heroVenue.id,
          placement: isMobile || isQuickSearch ? "center" : "side",
          node: (
            <HeroSearchWidget
              image={data.venueHeader?.image!}
              title={data.title!}
              description={data.descriptions?.description!}
              trackingType="Venue Card"
              actions={
                <>
                  <Button variant="default" href={data.uri} rightIcon="chevronRight">
                    More Info
                  </Button>
                  {data.info?.website && (
                    <Button variant="ghost" href={data.info.website} rightIcon="externalLink" target="_blank">
                      Visit Website
                    </Button>
                  )}
                  {data.info?.address && (
                    <Button
                      variant="ghost"
                      target="_blank"
                      href={
                        "https://www.google.com/maps/search/?api=1&query=" +
                        encodeURI(
                          data.title +
                            ", " +
                            [address.Street, address.Suburb, address.State, address.Country].filter(Boolean).join(" "),
                        )
                      }
                      rightIcon="link"
                    >
                      Google Maps
                    </Button>
                  )}
                  {data.info?.phone && (
                    <Button variant="ghost" href={"tel:" + data.info.phone} rightIcon="externalLink" target="_blank">
                      Call Venue
                    </Button>
                  )}
                </>
              }
              buttons={
                <Button variant="hero" shape="branded" href={"/program/by-date/?venues[]=" + data.slug} data-main-link>
                  Films showing here
                </Button>
              }
            />
          ),
        }
      }
    },
  },
  relatedFilms: {
    maybeGetWidget({ isMobile, isQuickSearch, documents, program }) {
      if (isQuickSearch || documents.length < 4) return
      const heroFilm = documents
        .slice(0, 2)
        .find((doc): doc is SFFRagDocumentResultTypes["film"] => doc.score > 0.45 && doc.type === "film")
      if (heroFilm) {
        return {
          score: documents[3].score,
          placement: "center",
          node: <RelatedFilmsWidget id={heroFilm.id} film={heroFilm.data!} />,
          maximum: 1,
          presentingResults: [],
          key: "relatedFilms_" + heroFilm.id,
        }
      }
    },
  },
  filmHeroCard: {
    maybeGetWidget({ isMobile, isQuickSearch, types, documents }) {
      if (isQuickSearch) return
      if (types?.length! > 0 && !types?.includes("film")) return
      const heroFilm = documents
        .slice(0, 4)
        .find((doc): doc is SFFRagDocumentResultTypes["film"] => doc.score > 0.4 && doc.type === "film")
      if (heroFilm) {
        const data = heroFilm.data
        const filmMeta = getFilmMeta(data)

        return {
          score: heroFilm.score * 1.1,
          maximum: 1,
          presentingResults: isMobile || isQuickSearch ? [heroFilm] : [],
          key: "heroFilmCard" + heroFilm.id,
          placement: isMobile || isQuickSearch ? "center" : "side",
          node: (
            <HeroSearchWidget
              image={data.media?.thumb!}
              title={data.title!}
              description={data.descriptions?.short!}
              trackingType="Film Card"
              actions={
                <>
                  {filmMeta.filter(Boolean).map((item, i) => (
                    <Chip shape="rounded" variant="mid" key={i}>
                      {item}
                    </Chip>
                  ))}
                </>
              }
              buttons={
                <>
                  <Button variant="primary" shape="branded" href={data.uri}>
                    More Info
                  </Button>
                  <Button variant="hero" shape="branded" href={data.uri + "#tickets"}>
                    Book Tickets
                  </Button>
                </>
              }
            />
          ),
        }
      }
    },
  },
  strandHeroCard: {
    maybeGetWidget({ isMobile, isQuickSearch, types, documents }) {
      if (types?.length! > 0 && !types?.includes("strand")) return
      const heroStrand = documents
        .slice(0, 4)
        .find((doc): doc is SFFRagDocumentResultTypes["strand"] => doc.score > 0.4 && doc.type === "strand")
      if (heroStrand) {
        const data = heroStrand.data
        return {
          score: heroStrand.score * 1.1,
          maximum: 1,
          presentingResults: isMobile || isQuickSearch ? [heroStrand] : [],
          key: "heroStrandCard" + heroStrand.id,
          placement: isMobile || isQuickSearch ? "center" : "side",
          node: (
            <HeroSearchWidget
              image={data.images?.[Math.min(data.images.length - 1, 3)]!}
              title={data.title!}
              description={data.excerpt!}
              trackingType="Strand Card"
              actions={
                <>
                  <Chip shape="rounded" variant="mid">
                    Program Strand
                  </Chip>
                  {data.info?.category?.name && (
                    <Chip shape="rounded" variant="mid">
                      {data.info?.category?.name}
                    </Chip>
                  )}
                  {data.films?.length! > 1 && (
                    <Chip shape="rounded" variant="mid">
                      {data.films?.length} Films
                    </Chip>
                  )}
                </>
              }
              buttons={
                <>
                  <Button variant="hero" shape="branded" href={data.uri}>
                    View Collection
                  </Button>
                </>
              }
            />
          ),
        }
      }
    },
  },
  adminCta: {
    maybeGetWidget({ isMobile, isQuickSearch, appData, types, documents }) {
      if (types?.length! > 0 && !types?.includes("ctas")) return
      const ctaMatch = documents
        .slice(0, 4)
        .find((doc): doc is SFFRagDocumentResultTypes["ctas"] => doc.score > 0.4 && doc.type === "ctas")
      if (ctaMatch) {
        const data = ctaMatch.data
        const block: ContentBlock =
          appData.templateParts?.searchPatterns?.contentBlocks.find(
            (block) => block?.inline?.cta_id === data.blockId,
          ) ?? data.block
        return {
          score: ctaMatch.score * 0.9,
          maximum: 1,
          presentingResults: [ctaMatch],
          key: "cta_block_" + ctaMatch.id,
          placement: isMobile || isQuickSearch ? "center" : "side",
          node: (
            <Suspense>
              <SingleContentBlock block={block} />
            </Suspense>
          ),
        }
      }
    },
  },
}
